import React from 'react';
import {
  Badge,
  Card, CardSkeleton, Dropdown, HStack, ICON_ALERT_CIRCLE_OUTLINE, ICON_CALENDAR_MONTH_OUTLINE, Message, SimpleTable, SimpleTableSkeleton,
  Stack,
} from '@doveit/bricks';
import { useHRCalendarEvents } from '../../hooks/use-hr-calendar-events/useHRCalendarEvents';
import { HRCalendarEvent, JobApplication } from '../../../providers/api/dtos';
import { hrCalendarEventsFilters } from './constants';
import UpsertHRRecallAction from '../upsert-hr-recall-action/UpsertHRRecallAction';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import { SortOrder } from '../../../providers/pagination';
import { MAX_PAGED_SIZE } from '../../../constants';
import UpsertHRInterviewAction from '../../../job-application/containers/upsert-hr-interview-action/UpsertHRInterviewAction';
import ViewHRInterviewCard from '../view-hr-interview-card/ViewHRInterviewCard';
import ViewHRRecallCard from '../view-hr-recall-card/ViewHRRecallCard';
import ViewHRInterviewTableRow from '../view-hr-interview-table-row/ViewHRInterviewTableRow';
import ViewHRRecallTableRow from '../view-hr-recall-table-row/ViewHRRecallTableRow';

export interface HRCalendarEventsWidgetProps extends React.AriaAttributes {
  jobApplication: JobApplication,
  canSetInterview?: boolean,
  canUpdateInterview?: boolean,
  canSetRecall?: boolean,
  canUpdateRecall?: boolean,
  canDeleteRecall?: boolean
  onSet?: (calendarEvent: HRCalendarEvent) => void,
}

const HRCalendarEventsWidget: React.FC<HRCalendarEventsWidgetProps> = ({
  jobApplication,
  canSetInterview = false,
  canUpdateInterview = false,
  canSetRecall = false,
  canUpdateRecall = false,
  canDeleteRecall,
  onSet,
  ...rest
}) => {
  const [activeFilter, setActiveFilter] = React.useState<keyof typeof hrCalendarEventsFilters>('all');

  const isMobile = useIsDevice('mobile');

  const canShow = React.useMemo(
    () => !!jobApplication.supervisorEmail,
    [jobApplication.supervisorEmail],
  );

  const {
    data: calendarEvents,
    isLoading: areCalendarEventsLoading,
    error: calendarEventsError,
    mutate: mutateCalendarEvents,
  } = useHRCalendarEvents(canShow ? {
    jobApplicationId: jobApplication.id,
    ...hrCalendarEventsFilters[activeFilter].filters,
  } : undefined, {
    size: MAX_PAGED_SIZE,
    sort: { date: SortOrder.DESC },
  });

  const handleCalendarEventSet = React.useCallback((calendarEvent: HRCalendarEvent) => {
    mutateCalendarEvents();

    onSet?.(calendarEvent);
  }, [mutateCalendarEvents, onSet]);

  if (!canShow) {
    return null;
  }

  if (areCalendarEventsLoading) {
    return (
      <CardSkeleton aria-label="Eventi calendario in caricamento">
        {!isMobile && <SimpleTableSkeleton />}
      </CardSkeleton>
    );
  }

  if (calendarEventsError) {
    return (
      <Card aria-label="Errore caricamento eventi calendario">
        <Card.Header
          icon={{ path: ICON_ALERT_CIRCLE_OUTLINE }}
          title="Calendario"
          color="critical"
        />
        <Card.Content>
          <Message message="Non è stato possibile caricare gli eventi del calendario." />
        </Card.Content>
      </Card>
    );
  }

  return (
    <Card aria-label="Eventi calendario" {...rest}>
      <Card.Header
        icon={{ path: ICON_CALENDAR_MONTH_OUTLINE }}
        title="Calendario"
        primaryActions={!isMobile ? [
          <UpsertHRRecallAction
            jobApplication={jobApplication}
            canCreate={canSetRecall}
            onSuccess={handleCalendarEventSet}
          />,
          <UpsertHRInterviewAction
            jobApplication={jobApplication}
            canCreate={canSetInterview}
            onSuccess={handleCalendarEventSet}
          />,
        ] : undefined}
        secondaryActions={isMobile ? [
          <UpsertHRRecallAction
            jobApplication={jobApplication}
            canCreate={canSetRecall}
            onSuccess={handleCalendarEventSet}
          >
            {({ openModal, isSaving }) => (
              <Dropdown.Option
                label="Fissa recall"
                onClick={openModal}
                loading={isSaving}
              />
            )}
          </UpsertHRRecallAction>,
          <UpsertHRInterviewAction
            jobApplication={jobApplication}
            canCreate={canSetInterview}
            onSuccess={handleCalendarEventSet}
          >
            {({ openModal, isSaving }) => (
              <Dropdown.Option
                label="Fissa colloquio"
                onClick={openModal}
                loading={isSaving}
              />
            )}
          </UpsertHRInterviewAction>,
        ] : undefined}
      />

      <Card.Content>
        <Stack aria-label="Lista eventi del calendario" gap={150}>
          <HStack aria-label="Filtri">
            {Object.keys(hrCalendarEventsFilters).map(((f) => {
              const filter = f as keyof typeof hrCalendarEventsFilters;

              return (
                <Badge
                  label={hrCalendarEventsFilters[filter].label}
                  aria-label={hrCalendarEventsFilters[filter].label}
                  key={filter}
                  emphasis="low"
                  onClick={filter !== activeFilter ? () => setActiveFilter(filter) : undefined}
                  color={activeFilter === filter ? 'primary' : 'neutral'}
                />
              );
            }))}
          </HStack>

          {calendarEvents?.content.length === 0 && (
            <Message
              type="neutral"
              message="Non sono presenti eventi in calendario."
            />
          )}

          {isMobile && calendarEvents?.content.map((calendarEvent) => {
            switch (calendarEvent.type) {
              case 'INTERVIEW':
                return (
                  <ViewHRInterviewCard
                    key={calendarEvent.id}
                    hrInterview={calendarEvent}
                    jobApplication={jobApplication}
                    canUpdate={canUpdateInterview}
                    onUpdate={() => mutateCalendarEvents()}
                  />
                );
              case 'RECALL':
                return (
                  <ViewHRRecallCard
                    key={calendarEvent.id}
                    hrRecall={calendarEvent}
                    jobApplication={jobApplication}
                    canUpdate={canUpdateRecall}
                    canDelete={canDeleteRecall}
                    onUpdate={() => mutateCalendarEvents()}
                    onDelete={() => mutateCalendarEvents()}
                  />
                );
              default:
                return null;
            }
          })}

          {!isMobile && !!calendarEvents?.content.length && (
            <SimpleTable>
              <SimpleTable.Body>
                {calendarEvents.content.map((calendarEvent) => (
                  <SimpleTable.Row
                    asChild
                    key={calendarEvent.id}
                  >
                    {calendarEvent.type === 'INTERVIEW' && (
                      <ViewHRInterviewTableRow
                        hrInterview={calendarEvent}
                        jobApplication={jobApplication}
                        canUpdate={canUpdateInterview}
                        onUpdate={() => mutateCalendarEvents()}
                      />
                    )}

                    {calendarEvent.type === 'RECALL' && (
                      <ViewHRRecallTableRow
                        hrRecall={calendarEvent}
                        jobApplication={jobApplication}
                        canUpdate={canUpdateRecall}
                        canDelete={canDeleteRecall}
                        onUpdate={() => mutateCalendarEvents()}
                        onDelete={() => mutateCalendarEvents()}
                      />
                    )}
                  </SimpleTable.Row>
                ))}
              </SimpleTable.Body>
            </SimpleTable>
          )}
        </Stack>
      </Card.Content>
    </Card>
  );
};

export default HRCalendarEventsWidget;
